import { commonService } from "@/services/commonService";
import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

const TIMEZONE_STORAGE_KEY = "CNC_TIMEZONE_PERSIST";

 type SelectedTimezone = {
  timeZoneDesc: string;
  timeZoneLabel: string;
  ianaTimeZone: string;
};

type TimezoneContextType = {
  timezone: SelectedTimezone | null;
  setTimezone: (tz: SelectedTimezone) => void;
};

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);
  export const extractAbbreviation = (label: string): string => {
    return label?.split(" ")?.[0] ?? "";
  };

  export const TimezoneProvider = ({ children }: { children: ReactNode }) => {
    const [timezone, setTimezoneState] = useState<SelectedTimezone | null>(() => {
    const raw = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as SelectedTimezone;
    } catch {
      // Old format (string)
      return null;
    }
  });

  useEffect(() => {
      if (!timezone) {
        console.error("Invariant violated: timezone became null");
      }
  }, [timezone]);


  useEffect(() => {
    const user = localStorage.getItem("Username");
    if (!user) return;

    if (timezone) return; // already set

    const initTimezone = async () => {
      try {
        const resolved = await resolveBrowserTimezone();
        if (resolved) updateTimezone(resolved);
      } catch (err) {
        console.error("Failed to initialize timezone:", err);
      }
    };

    initTimezone();
  }, [timezone, localStorage.getItem("Username")]);


  const updateTimezone = (tz: SelectedTimezone) => {
    setTimezoneState(tz);
    localStorage.setItem(TIMEZONE_STORAGE_KEY, JSON.stringify(tz));
  };


	return <TimezoneContext.Provider value={{ timezone, setTimezone: updateTimezone }}>{children}</TimezoneContext.Provider>;
};

const resolveBrowserTimezone = async (): Promise<SelectedTimezone | null> => {
  const browserIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezones = await commonService.GetTimeZones();

  // IANA match (authoritative)
  let match = timezones.find(
    tz => tz.ianaTimeZone === browserIana
  );

  // UTC offset fallback
  if (!match) {
    const offsetMinutes = -new Date().getTimezoneOffset();

    match = timezones.find(tz => {
      const m = tz.timeZoneLabel.match(/UTC([+-])(\d{2}):(\d{2})/);
      if (!m) return false;

      const sign = m[1] === "-" ? -1 : 1;
      const hours = parseInt(m[2], 10);
      const minutes = parseInt(m[3], 10);

      return sign * (hours * 60 + minutes) === offsetMinutes;
    });
  }

  // Hard fallback → UTC
  if (!match) {
    match = timezones.find(tz => tz.ianaTimeZone === "Etc/UTC");
  }

  return match
    ? {
        timeZoneDesc: match.timeZoneDesc,
        timeZoneLabel: match.timeZoneLabel,
        ianaTimeZone: match.ianaTimeZone,
      }
    : null;
};


export const useTimezone = (): TimezoneContextType => {
	const context = useContext(TimezoneContext);
	if (!context) {
		throw new Error("useTimezone must be used within a TimezoneProvider");
	}
	return context;
};
